// src/app/demo/SnapSiteForm.tsx
// Esta es una copia de src/components/dashboard/SnapSiteForm.tsx, modificada para el modo DEMO.
"use client";

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useForm, type SubmitHandler, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import {
  createOrUpdateSnapSiteAction, // No se usará en demo mode
  deleteSnapSiteAction,       // No se usará en demo mode
  checkUsernameAvailability,  // No se usará en demo mode
} from '@/actions/snapsiteActions';
import type { SnapSiteData, SnapSiteFormInput, PersonalLink, SocialLinks, ThemeIdentifier } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Globe, Twitter, Instagram, Save, UserPlus, Link as LinkIconLucide,
  MessageSquare, Trash2, PlusCircle, Phone as PhoneIcon, Building as BuildingIcon, Briefcase, MapPin, UploadCloud,
  Image as ImageIconLucide, XCircle as XCircleIconLucide, Coffee, Loader2, CheckCircle2, Palette, AlertTriangle,
  Facebook, Youtube, PlaySquare, Edit3, RefreshCw // Added RefreshCw for demo
} from 'lucide-react';
import NextImage from 'next/image';
import { useRouter } from 'next/navigation';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import NextLink from 'next/link';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { cn } from '@/lib/utils';
import { auth } from '@/lib/firebase/client';
import { signOut as firebaseSignOut } from "firebase/auth";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import DonationModal from '@/components/premium/DonationModal';
import { Type } from 'lucide-react';
import { FONT_PAIRS, FONT_ORDER, buildFontFaceCSS } from '@/lib/snapsite-fonts';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const copyableBlockSchema = z.object({
  id: z.string(),
  icon: z.enum(['bank', 'copy']),
  title: z.string().min(1).max(40),
  fields: z.array(z.object({
    name: z.string().min(1).max(30),
    value: z.string().min(1).max(100),
  })).min(1).max(2),
});

const snapSiteSchema = z.object({
  username: z.string().min(3, 'El nombre de usuario debe tener al menos 3 caracteres.').regex(/^[a-z0-9-]+$/, 'Solo minúsculas, números y guiones.'),
  name: z.string().min(1, 'El nombre es obligatorio.'),
  bio: z.string().max(500, 'La biografía no puede exceder los 500 caracteres.').optional().nullable(),
  profileImage: z.any().optional(),
  removeProfileImage: z.boolean().optional(),
  currentProfileImageUrl: z.string().url().optional().nullable(),
  phone: z.string().max(50, "El teléfono no puede exceder los 50 caracteres.").optional().nullable(),
  company: z.string().max(100, "La empresa no puede exceder los 100 caracteres.").optional().nullable(),
  position: z.string().max(100, "El cargo no puede exceder los 100 caracteres.").optional().nullable(),
  location: z.string().max(100, "La ubicación no puede exceder los 100 caracteres.").optional().nullable(),
  googleMapsLink: z.string().url("Debe ser una URL válida.").or(z.literal('')).refine(
    (val) => val === '' || val.startsWith('https://maps.app.goo.gl/') || val.startsWith('https://www.google.com/maps/'),
    { message: "Por favor, introduce un enlace válido de Google Maps." }
  ).optional().nullable(),
  socialLinks: z.object({
    website: z.string().url({ message: "URL de sitio web inválida." }).or(z.literal('')).optional().nullable(),
    facebook: z.string().url({ message: "URL de Facebook inválida." }).or(z.literal('')).optional().nullable(),
    youtube: z.string().url({ message: "URL de YouTube inválida." }).or(z.literal('')).optional().nullable(),
    instagram: z.string().url({ message: "URL de Instagram inválida." }).or(z.literal('')).optional().nullable(),
    tiktok: z.string().url({ message: "URL de TikTok inválida." }).or(z.literal('')).optional().nullable(),
    twitter: z.string().url({ message: "URL de X (Twitter) inválida." }).or(z.literal('')).optional().nullable(),
    whatsapp: z.string().refine(val => {
      if (!val || val.trim() === '') return true;
      if (val.startsWith('https://wa.me/')) return z.string().url().safeParse(val).success;
      return /^\+?\d[\d\s-()]*\d$/.test(val);
    }, { message: "URL de WhatsApp inválida (ej. https://wa.me/numero o solo el número)."}).or(z.literal('')).optional().nullable(),
  }).optional(),
  personalLinks: z.array(
    z.object({
      name: z.string().min(1, "El nombre del enlace es obligatorio.").max(50, "Nombre del enlace demasiado largo."),
      url: z.string().url({ message: "URL inválida."}).min(1, "La URL del enlace es obligatoria."),
    })
  ).max(5, "Puedes añadir un máximo de 5 enlaces personales.").optional().default([]),
  acceptTerms: z.boolean().optional(), // No se usará en demo
  isPremium: z.boolean().optional().default(false),
  theme: z.string().optional().default('default'),
  accentColor: z.string().optional().default('default'),
  primaryColor: z.string().optional().default('default'),
  layout: z.string().optional().default('classic'),
  usePhoneForWhatsapp: z.boolean().optional().default(false),
  copyableBlocks: z.array(copyableBlockSchema).optional(),
  hideContactEmail: z.boolean().optional().default(false),
  animateIcons: z.boolean().optional().default(false),
  whatsappShareEnabled: z.boolean().optional().default(false),
  fontPair: z.string().optional().default('default'),
});

const themeOptions: { label: string; value: ThemeIdentifier; dotColor?: string, previewColors: { bg: string, card: string, primary: string, text: string } }[] = [
  { label: "Claro (Default)", value: "default", dotColor: "hsl(30 62% 72%)", previewColors: { bg: "hsl(240 14% 95%)", card: "hsl(240 14% 98%)", primary: "hsl(30 62% 72%)", text: "hsl(220 9% 20%)" } },
  { label: "Gris Oscuro", value: "gris-oscuro", dotColor: "hsl(45 90% 60%)", previewColors: { bg: "hsl(220 10% 12%)", card: "hsl(220 10% 18%)", primary: "hsl(45 90% 60%)", text: "hsl(220 10% 85%)" } },
  { label: "Azul Petróleo", value: "azul-petroleo", dotColor: "hsl(30 80% 65%)", previewColors: { bg: "hsl(195 35% 15%)", card: "hsl(195 35% 22%)", primary: "hsl(30 80% 65%)", text: "hsl(195 20% 90%)" } },
  { label: "Verde Corporativo", value: "verde-corporate", dotColor: "hsl(90 50% 75%)", previewColors: { bg: "hsl(145 45% 20%)", card: "hsl(145 40% 28%)", primary: "hsl(90 50% 75%)", text: "hsl(145 15% 92%)" } },
  { label: "Rosa Suave", value: "rosa-suave", dotColor: "hsl(320 60% 65%)", previewColors: { bg: "hsl(340 70% 97%)", card: "hsl(340 80% 94%)", primary: "hsl(320 60% 65%)", text: "hsl(340 50% 20%)" } },
  { label: "Ámbar", value: "ambar", dotColor: "hsl(30 90% 55%)", previewColors: { bg: "hsl(38 95% 90%)", card: "hsl(38 90% 85%)", primary: "hsl(30 90% 55%)", text: "hsl(35 70% 25%)" } },
  { label: "Morado Lavanda", value: "morado-lavanda", dotColor: "hsl(275 50% 60%)", previewColors: { bg: "hsl(260 60% 96%)", card: "hsl(260 70% 92%)", primary: "hsl(275 50% 60%)", text: "hsl(260 40% 25%)" } },
  { label: "Azul Suave", value: "azul-suave", dotColor: "hsl(215 70% 60%)", previewColors: { bg: "hsl(205 70% 97%)", card: "hsl(205 80% 93%)", primary: "hsl(215 70% 60%)", text: "hsl(205 50% 20%)" } },
];

interface SnapSiteFormProps {
  initialData?: SnapSiteData | null;
  isDemoMode?: boolean;
  onDemoFormUpdate?: (data: SnapSiteData) => void;
  demoInitialPremiumState?: boolean;
}

export default function SnapSiteForm({ 
  initialData: initialDataProp,
  isDemoMode = false,
  onDemoFormUpdate,
  demoInitialPremiumState = false
}: SnapSiteFormProps) {
  const { user } = useAuth(); // Se usa para la UI de "Apoyar con un café", no para auth en modo demo
  const { toast } = useToast();
  const router = useRouter(); // No se usa para redirección en modo demo
  const [loading, setLoading] = useState(false);
  // currentSnapSiteData se usa para la UI del botón de café y estado premium
  const [currentSnapSiteData, setCurrentSnapSiteData] = useState<SnapSiteData | null | undefined>(initialDataProp); 
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const { executeRecaptcha } = useGoogleReCaptcha(); // No se usa en modo demo

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false); // No se usa en modo demo
  const [deleteConfirmationText, setDeleteConfirmationText] = useState(''); // No se usa en modo demo
  const [isDeleting, setIsDeleting] = useState(false); // No se usa en modo demo

  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
  
  const isEditing = !!(initialDataProp?.username && !isDemoMode); // En modo demo, siempre es como "creación" local
  
  const {
    register, handleSubmit, control, watch, setValue, reset, formState: { errors, isDirty }, getValues
  } = useForm<SnapSiteFormInput>({
    resolver: zodResolver(snapSiteSchema),
    // Los defaultValues se manejan en el useEffect para adaptarse al modo demo o real
  });
  
  const watchedUsername = watch('username');
  const usernameForDisplayAndActions = isDemoMode ? watchedUsername : (initialDataProp?.username || watchedUsername);
  const profileImageFileWatch = watch('profileImage'); 
  const acceptTermsWatch = watch('acceptTerms'); // No se usa en demo
  const phoneWatch = watch('phone');
  const usePhoneForWhatsappWatch = watch('usePhoneForWhatsapp');

  const imageInputRef = useRef<HTMLInputElement>(null);

  // En modo demo, el username siempre es editable (localmente) y no se verifica.
  // En modo real, se comporta como antes.
  const usernameIsEditable = isDemoMode ? true : (!isEditing || (isEditing && initialDataProp?.username === initialDataProp?.ownerUID));
  
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(isDemoMode ? true : (isEditing && !usernameIsEditable ? true : null));
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameFeedback, setUsernameFeedback] = useState<string>(
    isDemoMode ? 'Este nombre de usuario es solo para la demo.' : (usernameIsEditable && !isEditing ? 'Mínimo 3 caracteres, solo minúsculas, números y guiones.' : '')
  );
  const usernameCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const formIsPremiumForUI = isDemoMode ? demoInitialPremiumState : (currentSnapSiteData?.isPremium || false);

  useEffect(() => {
    const defaultValues: SnapSiteFormInput = {
      username: initialDataProp?.username || (isDemoMode ? 'usuario-demo' : ''),
      name: initialDataProp?.name || (isDemoMode ? 'Nombre Demo' : ''),
      bio: initialDataProp?.bio || (isDemoMode ? 'Esta es una biografía de demostración.' : ''),
      phone: initialDataProp?.phone || '',
      company: initialDataProp?.company || '',
      position: initialDataProp?.position || '',
      location: initialDataProp?.location || '',
      googleMapsLink: initialDataProp?.googleMapsLink || '',
      socialLinks: initialDataProp?.socialLinks || {
        website: '', facebook: '', youtube: '', instagram: '', tiktok: '', twitter: '', whatsapp: '',
      },
      personalLinks: initialDataProp?.personalLinks || [],
      removeProfileImage: false,
      profileImage: undefined,
      currentProfileImageUrl: initialDataProp?.profileImageUrl || null,
      acceptTerms: isDemoMode ? true : !!initialDataProp, // No se usa en demo, pero RHF lo necesita
      isPremium: isDemoMode ? demoInitialPremiumState : (initialDataProp?.isPremium || false),
      theme: initialDataProp?.theme || 'default',
      accentColor: initialDataProp?.accentColor || initialDataProp?.theme || 'default',
      primaryColor: initialDataProp?.primaryColor || initialDataProp?.theme || 'default',
      layout: initialDataProp?.layout || 'classic',
      usePhoneForWhatsapp: !!(initialDataProp?.socialLinks?.whatsapp?.startsWith('https://wa.me/') &&
                            initialDataProp?.phone &&
                            initialDataProp.socialLinks.whatsapp.includes(initialDataProp.phone.replace(/\D/g, ''))),
      hideContactEmail: initialDataProp?.hideContactEmail || false,
      animateIcons: initialDataProp?.animateIcons || false,
      whatsappShareEnabled: initialDataProp?.whatsappShareEnabled || false,
      fontPair: initialDataProp?.fontPair || 'default',
    };
    reset(defaultValues);
    setCurrentSnapSiteData(initialDataProp); // Para UI de premium, etc.
    setPreviewImage(initialDataProp?.profileImageUrl || null);
    
    if (isDemoMode) {
      setUsernameAvailable(true);
      setUsernameFeedback('Este nombre de usuario es solo para la demo.');
    } else if (usernameIsEditable && !isEditing) {
        setUsernameAvailable(null);
        setUsernameFeedback('Mínimo 3 caracteres, solo minúsculas, números y guiones.');
    } else if (isEditing && !usernameIsEditable) {
        setUsernameAvailable(true);
        setUsernameFeedback('');
    } else if (isEditing && usernameIsEditable) { // Cubrir el caso de UID como username
        setUsernameAvailable(null);
        setUsernameFeedback('Mínimo 3 caracteres, solo minúsculas, números y guiones.');
    }

  }, [initialDataProp, reset, isDemoMode, demoInitialPremiumState, usernameIsEditable, isEditing]);
  
  // Carga lazy de todas las fuentes para previews en demo
  useEffect(() => {
    if (!isDemoMode) return;
    const allCSS = FONT_ORDER
      .filter(k => k !== 'default')
      .map(k => {
        const p = FONT_PAIRS[k];
        return p ? buildFontFaceCSS(p) : '';
      })
      .join('');
    if (!allCSS) return;
    const styleId = 'snapsite-demo-font-previews';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = allCSS;
      document.head.appendChild(style);
    }
  }, [isDemoMode]);


  useEffect(() => {
    // Manejo de preview de imagen
    const removeProfileImageFlag = watch('removeProfileImage');
    const currentProfileImageFileList = getValues('profileImage') as FileList | undefined;
  
    let newPreviewUrl: string | null = null;
  
    if (removeProfileImageFlag) {
      newPreviewUrl = null;
    } else if (currentProfileImageFileList && currentProfileImageFileList.length > 0 && currentProfileImageFileList[0] instanceof File) {
      newPreviewUrl = URL.createObjectURL(currentProfileImageFileList[0]);
    } else if (initialDataProp?.profileImageUrl) { // Usar initialDataProp aquí para consistencia en demo
      newPreviewUrl = initialDataProp.profileImageUrl;
    }
  
    const oldPreview = previewImage;
    setPreviewImage(newPreviewUrl);
  
    return () => {
      if (oldPreview && oldPreview.startsWith('blob:') && newPreviewUrl !== oldPreview) {
        URL.revokeObjectURL(oldPreview);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [profileImageFileWatch, watch('removeProfileImage'), initialDataProp?.profileImageUrl, getValues]);
  

  useEffect(() => {
    // Lógica de verificación de username, solo si NO es demo mode
    if (isDemoMode) {
      setUsernameFeedback('Este nombre de usuario es solo para la demo.');
      setUsernameAvailable(true);
      setIsCheckingUsername(false);
      if (usernameCheckTimeoutRef.current) clearTimeout(usernameCheckTimeoutRef.current);
      return;
    }

    if (!usernameIsEditable) {
      setUsernameFeedback(isEditing ? '' : 'Mínimo 3 caracteres, solo minúsculas, números y guiones.');
      setUsernameAvailable(isEditing ? true : null);
      setIsCheckingUsername(false);
      if (usernameCheckTimeoutRef.current) clearTimeout(usernameCheckTimeoutRef.current);
      return;
    }

    if (!watchedUsername || watchedUsername.length < 3 || !/^[a-z0-9-]+$/.test(watchedUsername)) {
      setUsernameFeedback(watchedUsername ? 'Solo minúsculas, números y guiones.' : 'Mínimo 3 caracteres, solo minúsculas, números y guiones.');
      setUsernameAvailable(false);
      setIsCheckingUsername(false);
      if (usernameCheckTimeoutRef.current) clearTimeout(usernameCheckTimeoutRef.current);
      return;
    }
    
    setIsCheckingUsername(true);
    setUsernameFeedback('Comprobando disponibilidad...');
    setUsernameAvailable(null);
    if (usernameCheckTimeoutRef.current) clearTimeout(usernameCheckTimeoutRef.current);

    usernameCheckTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await checkUsernameAvailability(watchedUsername);
        setUsernameFeedback(result.message);
        setUsernameAvailable(result.available);
      } catch (error) {
        setUsernameFeedback('Error al verificar. Inténtalo de nuevo.');
        setUsernameAvailable(false);
      } finally {
        setIsCheckingUsername(false);
      }
    }, 500); 
    
    return () => { if (usernameCheckTimeoutRef.current) clearTimeout(usernameCheckTimeoutRef.current); };
  }, [watchedUsername, usernameIsEditable, isEditing, initialDataProp?.username, initialDataProp?.ownerUID, isDemoMode]);

  const handleUrlBlur = (e: React.FocusEvent<HTMLInputElement>, fieldName: any) => {
    let value = e.target.value.trim();
    if (value && !value.startsWith('http://') && !value.startsWith('https://') && value.includes('.')) {
      setValue(fieldName, `https://${value}`, { shouldValidate: true, shouldDirty: true });
    }
  };
  
  const handleWhatsappBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    let value = e.target.value.trim();
    if (value && /^\+?\d[\d\s-()]*\d$/.test(value) && !value.toLowerCase().startsWith('http') && !value.toLowerCase().startsWith('wa.me')) { 
        setValue('socialLinks.whatsapp', `https://wa.me/${value.replace(/\D/g, '')}`, { shouldValidate: true, shouldDirty: true });
    } else if (value && !value.startsWith('http://') && !value.startsWith('https://') && value.includes('.') && !value.toLowerCase().startsWith('wa.me')) { 
        setValue('socialLinks.whatsapp', `https://${value}`, { shouldValidate: true, shouldDirty: true });
    }
  };

  useEffect(() => {
    if (usePhoneForWhatsappWatch && phoneWatch) {
      const cleanPhoneNumber = phoneWatch.replace(/\D/g, ''); 
      if (cleanPhoneNumber) {
        setValue('socialLinks.whatsapp', `https://wa.me/${cleanPhoneNumber}`, { shouldDirty: true, shouldValidate: true });
      }
    } else if (!usePhoneForWhatsappWatch && phoneWatch && getValues('socialLinks.whatsapp') === `https://wa.me/${phoneWatch.replace(/\D/g, '')}`) {
      const previousManualWhatsapp = initialDataProp?.socialLinks?.whatsapp !== `https://wa.me/${initialDataProp?.phone?.replace(/\D/g, '')}` ? initialDataProp?.socialLinks?.whatsapp : '';
      setValue('socialLinks.whatsapp', previousManualWhatsapp || '', { shouldDirty: true, shouldValidate: true });
    }
  }, [usePhoneForWhatsappWatch, phoneWatch, setValue, getValues, initialDataProp?.socialLinks?.whatsapp, initialDataProp?.phone]);


  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const lowercaseValue = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setValue('username', lowercaseValue, { shouldValidate: true, shouldDirty: true });
  };

  const { fields: personalLinkFields, append: appendPersonalLink, remove: removePersonalLink } = useFieldArray({
    control, name: "personalLinks",
  });

  // handlePaymentSuccess se mantiene para la UI de "Apoyar con café", que puede estar presente
  // incluso en la demo, aunque no afectaría el estado 'isPremium' de la demo directamente (eso lo maneja el switch).
  const handlePaymentSuccess = useCallback(async (details: { transactionId: string; amount: number; paypalOrderId: string }) => {
    console.log('[SnapSiteForm] PayPal Payment Success Details (Demo or Real):', details);
    if (isDemoMode) {
        toast({
            title: '¡Gracias por tu apoyo (Demo)!',
            description: "En un escenario real, tus funciones premium se activarían aquí.",
        });
        setIsDonationModalOpen(false);
        return;
    }
    // Lógica original para modo real
    if (!user) {
        toast({ variant: 'destructive', title: 'Error', description: 'Usuario no autenticado para activar premium.' });
        setIsDonationModalOpen(false);
        return;
    }
    setLoading(true); 
    try {
        const response = await fetch('/api/premium/activate', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                transactionId: details.transactionId,
                amount: details.amount,
                paypalOrderId: details.paypalOrderId,
            }),
            credentials: 'include', 
        });
        const result = await response.json();
        if (!response.ok || !result.success) {
            throw new Error(result.message || 'Error al activar funciones premium.');
        }
        toast({
          title: '¡Éxito!',
          description: (
            <div className="flex items-center space-x-3">
              <NextImage
                src="/devmonkeys/devmonkey_coffe_happy_1_1.webp"
                alt="DevMonkey feliz con café"
                width={80}
                height={80}
                className="rounded-lg shadow-md"
                data-ai-hint="monkey happy coffee"
                unoptimized
              />
              <span>¡Funciones premium activadas! Tu perfil se actualizará.</span>
            </div>
          ),
        });
        
        setCurrentSnapSiteData(prev => prev ? ({ ...prev, isPremium: true, status: 'published' }) : null);
        reset(getValues(), { keepValues: true, keepDirty: false }); 
        router.refresh(); 

    } catch (error: any) {
        console.error('[SnapSiteForm] Error de Activación Premium:', error);
        toast({ variant: 'destructive', title: 'Error de Activación Premium', description: error.message || 'No se pudieron activar las funciones premium.' });
    } finally {
        setIsDonationModalOpen(false);
        setLoading(false); 
    }
  }, [user, toast, router, reset, getValues, setCurrentSnapSiteData, isDemoMode]); 


  const onSubmit: SubmitHandler<SnapSiteFormInput> = async (data) => {
    if (isDemoMode) {
      if (onDemoFormUpdate) {
        const demoSnapSiteData: SnapSiteData = {
          ownerUID: 'demo-user-uid',
          ownerEmail: 'demo@example.com',
          username: data.username.trim(),
          name: data.name,
          bio: data.bio || '',
          profileImageStoragePath: null, // No se usa en demo
          profileImageUrl: previewImage, // Usar la URL del blob local
          socialLinks: {
            website: data.socialLinks?.website || '',
            facebook: data.socialLinks?.facebook || '',
            youtube: data.socialLinks?.youtube || '',
            instagram: data.socialLinks?.instagram || '',
            tiktok: data.socialLinks?.tiktok || '',
            twitter: data.socialLinks?.twitter || '',
            whatsapp: data.socialLinks?.whatsapp || '',
          },
          personalLinks: data.personalLinks || [],
          last_updated: new Date().toISOString(),
          status: 'published', // En demo, siempre se muestra como publicado
          phone: data.phone || null,
          company: data.company || null,
          position: data.position || null,
          location: data.location || null,
          googleMapsLink: data.googleMapsLink || null,
          isPremium: demoInitialPremiumState, // Usar el estado del switch de la página demo
          theme: data.theme || 'default',
          accentColor: data.accentColor || 'default',
          primaryColor: data.primaryColor || 'default',
          layout: data.layout || 'classic',
          fontPair: data.fontPair || 'default',
        };
        onDemoFormUpdate(demoSnapSiteData);
        toast({ title: 'Vista Previa Actualizada (Demo)', description: 'Los cambios se reflejan en la vista previa.' });
      }
      return;
    }

    // Lógica original para modo real
    if (!user) {
      toast({ variant: 'destructive', title: 'No Autenticado', description: 'Por favor, inicia sesión para gestionar tu SnapSite.' });
      return;
    }
    if (!isEditing && !acceptTermsWatch) {
      toast({ variant: 'destructive', title: 'Términos y Condiciones', description: 'Debes aceptar los términos para crear tu SnapSite.' });
      return;
    }
    if (usernameIsEditable && (usernameAvailable === false || isCheckingUsername)) {
      toast({ variant: 'destructive', title: 'Nombre de Usuario Inválido', description: 'Elige un nombre de usuario válido y disponible.' });
      return;
    }
    
    let recaptchaToken;
    if (!executeRecaptcha) {
      console.warn("[SnapSiteForm] reCAPTCHA not ready");
      toast({ variant: "destructive", title: "Error de Seguridad", description: "El servicio de reCAPTCHA no está listo. Intenta de nuevo." });
      return;
    }
    setLoading(true);
    try {
      recaptchaToken = await executeRecaptcha('submitSnapSite');
      if (!recaptchaToken) {
        toast({ variant: "destructive", title: "Error de Seguridad", description: "No se pudo obtener el token de reCAPTCHA." });
        setLoading(false); return;
      }
    } catch (e: any) {
      console.error("[SnapSiteForm] Error obtaining reCAPTCHA token:", e);
      toast({ variant: "destructive", title: "Error de Seguridad", description: e.message || "No se pudo obtener el token de reCAPTCHA." });
      setLoading(false); return;
    }

    const formDataPayload = new FormData();
    formDataPayload.append('username', data.username.trim());
    formDataPayload.append('initialUsername', initialDataProp?.username || ''); 
    formDataPayload.append('name', data.name);
    if (data.bio) formDataPayload.append('bio', data.bio);
    formDataPayload.append('phone', data.phone || '');
    formDataPayload.append('company', data.company || '');
    formDataPayload.append('position', data.position || '');
    formDataPayload.append('location', data.location || '');
    formDataPayload.append('googleMapsLink', data.googleMapsLink || '');
    
    const profileImageFileList = data.profileImage as FileList | null | undefined;
    if (profileImageFileList && profileImageFileList.length > 0 && profileImageFileList[0] instanceof File) {
        const file = profileImageFileList[0];
        if (file.size > MAX_FILE_SIZE) {
          toast({ variant: "destructive", title: "Archivo Demasiado Grande", description: `La imagen no debe exceder los ${MAX_FILE_SIZE / (1024 * 1024)} MB.`});
          setLoading(false); return;
        }
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
          toast({ variant: "destructive", title: "Tipo de Archivo Inválido", description: "Solo se aceptan imágenes JPEG, PNG, o WebP." });
          setLoading(false); return;
        }
        formDataPayload.append('profileImage', file);
    }
    
    formDataPayload.append('removeProfileImage', String(data.removeProfileImage || false));
    formDataPayload.append('recaptchaToken', recaptchaToken);
    formDataPayload.append('isPremium', String(data.isPremium || currentSnapSiteData?.isPremium || false));
    formDataPayload.append('theme', data.theme || currentSnapSiteData?.theme || 'default');

    const socialKeys: (keyof SocialLinks)[] = ['website', 'facebook', 'youtube', 'instagram', 'tiktok', 'twitter', 'whatsapp'];
    socialKeys.forEach(key => {
        const formValue = data.socialLinks?.[key];
        formDataPayload.append(`socialLinks.${key}`, formValue || '');
    });

    (data.personalLinks || []).forEach((link, index) => {
      if (link.name && link.url) { 
        formDataPayload.append(`personalLinks[${index}].name`, link.name);
        formDataPayload.append(`personalLinks[${index}].url`, link.url);
      }
    });
    
    let idToken;
    try { idToken = await user.getIdToken(true); } 
    catch (tokenError: any) {
      toast({ variant: 'destructive', title: 'Error de Autenticación', description: tokenError.message || 'No se pudo verificar tu sesión.' });
      setLoading(false); return;
    }

    const result = await createOrUpdateSnapSiteAction(formDataPayload, idToken);
    setLoading(false);

    if (result.success && result.updatedSnapSiteData) {
      toast({
        title: '¡Éxito!',
        description: (
          <>
            {result.message}
            {result.snapSiteUrl && (
              <p className="mt-2">
                <a
                  href={result.snapSiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-primary hover:text-primary/80"
                >
                  Haz click aquí para abrir tu página.
                </a>
              </p>
            )}
          </>
        ),
        duration: 9000,
      });

      if (result.snapSiteUrl) { window.open(result.snapSiteUrl, '_blank'); }
      setCurrentSnapSiteData(result.updatedSnapSiteData); 
      reset(result.updatedSnapSiteData);
      if ((!isEditing || (isEditing && initialDataProp?.username !== result.updatedSnapSiteData.username)) && result.updatedSnapSiteData.username) {
        router.push('/dashboard'); 
        router.refresh(); 
      } else {
        router.refresh(); 
      }
    } else {
      toast({ variant: 'destructive', title: 'Error', description: result.message || "Ocurrió un error." });
    }
  };

  const handleDeleteSnapSite = async () => {
    // No se usa en modo demo
  };

  const socialLinkFieldsInfo: { name: keyof SocialLinks; label: string; icon: React.ElementType; placeholder: string; }[] = [
    { name: 'website', label: 'Sitio Web', icon: Globe, placeholder: 'https://ejemplo.com' },
    { name: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/tuUsuario' },
    { name: 'youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/tuCanal' },
    { name: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/tuUsuario' },
    { name: 'tiktok', label: 'TikTok', icon: PlaySquare, placeholder: 'https://tiktok.com/@tuUsuario' },
    { name: 'twitter', label: 'X (antes Twitter)', icon: Twitter, placeholder: 'https://x.com/tuUsuario' },
    { name: 'whatsapp', label: 'WhatsApp (Enlace wa.me o número)', icon: MessageSquare, placeholder: 'ej. +1234567890 o https://wa.me/...' },
  ];
  
  const baseUserDomain = process.env.NEXT_PUBLIC_USER_SNAPSITE_BASE_URL?.replace(/^https?:\/\//, '') || 'snapsite.cc';
  const isSubmitDisabled = loading || 
                           Object.keys(errors).length > 0 || 
                           (!isDemoMode && !isEditing && !acceptTermsWatch) || 
                           (!isDemoMode && usernameIsEditable && (usernameAvailable === false || isCheckingUsername)) ||
                           (!isDemoMode && !isDirty && isEditing);

  const paypalClientIdFromEnv = process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID;

  // Helper function to render the premium section or theme selector
  const renderPremiumSection = () => {
    if (isDemoMode) {
      // En modo demo, el control de premium/temas viene de demoInitialPremiumState
      if (demoInitialPremiumState) { // Simula premium
        return (
            <div className="p-4 border rounded-md bg-card mt-4">
              <h3 className="text-md font-semibold text-foreground mb-3 flex items-center">
                <Palette className="mr-2 h-5 w-5 text-primary" />
                Tema del Perfil (Demo Premium)
              </h3>
              <Controller
                name="theme"
                control={control}
                defaultValue={initialDataProp?.theme || 'default'}
                render={({ field }) => (
                  <RadioGroup onValueChange={field.onChange} value={field.value} className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                    {themeOptions.map((option) => (
                      <div key={option.value}>
                        <RadioGroupItem value={option.value} id={`theme-${option.value}`} className="sr-only peer" />
                        <Label htmlFor={`theme-${option.value}`} className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer transition-all">
                           <div className="flex items-center justify-center w-full h-6 mb-1.5 rounded-sm border overflow-hidden" style={{ backgroundColor: option.previewColors.card }}>
                              <div style={{ backgroundColor: option.previewColors.bg }} className="w-1/3 h-full"></div>
                              <div style={{ backgroundColor: option.previewColors.primary }} className="w-1/3 h-full"></div>
                              <div style={{ color: option.previewColors.text, backgroundColor: option.previewColors.card, display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="w-1/3 h-full text-xs">Aa</div>
                          </div>
                          <span className="text-xs text-center">{option.label}</span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
              />
              {errors.theme && <p className="text-sm text-destructive mt-1">{errors.theme?.message}</p>}

              {/* Selector de Fuentes en Demo */}
              <div className="mt-8 border-t pt-6">
                <h3 className="text-md font-semibold text-foreground mb-3 flex items-center">
                  <Type className="mr-2 h-5 w-5 text-primary" />
                  Tipografía (Demo Premium)
                </h3>
                <Controller
                  name="fontPair"
                  control={control}
                  render={({ field }) => (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {FONT_ORDER.map((key) => {
                        const pair = FONT_PAIRS[key];
                        if (!pair) return null;
                        const isSelected = (field.value || 'default') === key;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => field.onChange(key)}
                            className={cn(
                              "flex flex-col items-start p-3 rounded-lg border-2 transition-all text-left hover:shadow-sm",
                              isSelected ? "border-primary bg-primary/5" : "border-muted hover:border-primary/40"
                            )}
                            aria-pressed={isSelected}
                          >
                            <span 
                              className="text-base font-semibold leading-tight text-foreground truncate w-full"
                              style={{ fontFamily: pair.heading.family !== 'Inter' ? `'${pair.heading.family}'` : undefined }}
                            >
                              {pair.label}
                            </span>
                            <span 
                              className="text-[11px] text-muted-foreground mt-0.5 truncate w-full"
                              style={{ fontFamily: pair.body.family !== 'Inter' ? `'${pair.body.family}'` : undefined }}
                            >
                              {pair.description}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                />
              </div>
            </div>
        );
      } else { // Simula no premium
        return (
            <Alert variant="default" className="mt-4 mb-6 bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-400">
            <Coffee className="h-5 w-5 text-amber-600 dark:text-amber-500" />
            <AlertTitle className="font-semibold">✨ ¡Desbloquea Funciones Premium!</AlertTitle>
             <AlertDescription>
               <div className="flex flex-col sm:flex-row items-center gap-4">
                  <div className="flex-grow">
                      <p className="mb-2">Obtén publicación instantánea y más temas de personalización (¡como este selector!).</p>
                      {paypalClientIdFromEnv ? (
                          <Button 
                              type="button" 
                              size="sm" 
                              className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white" 
                              onClick={() => setIsDonationModalOpen(true)}
                          >
                              Apóyanos con un Café (Demo)
                          </Button>
                      ) : (
                          <p className="text-xs font-medium mt-2">(Opción de apoyo no disponible temporalmente.)</p>
                      )}
                  </div>
                  <div className="flex-shrink-0 order-first sm:order-last">
                      <NextImage
                          src="/devmonkeys/devmonkey_coffe1_1.webp"
                          alt="Mono desarrollador disfrutando un café"
                          width={80}
                          height={80}
                          className="rounded-lg shadow-md"
                          unoptimized
                          data-ai-hint="monkey developer coffee"
                      />
                  </div>
               </div>
            </AlertDescription>
          </Alert>
        );
      }
    }

    // Lógica original para modo real
    if (!currentSnapSiteData?.isPremium) {
      return (
           <Alert variant="default" className="mt-4 mb-6 bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-400">
            <Coffee className="h-5 w-5 text-amber-600 dark:text-amber-500" />
            <AlertTitle className="font-semibold">✨ ¡Desbloquea Funciones Premium!</AlertTitle>
             <AlertDescription>
               <div className="flex flex-col sm:flex-row items-center gap-4">
                  <div className="flex-grow">
                      <p className="mb-2">Obtén beneficios Pro como publicación instantánea y más temas de personalización.</p>
                      {paypalClientIdFromEnv ? (
                          <Button 
                              type="button" 
                              size="sm" 
                              className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white" 
                              onClick={() => setIsDonationModalOpen(true)}
                          >
                              Apóyanos con un Café y Desbloquea
                          </Button>
                      ) : (
                          <p className="text-xs font-medium mt-2">(Opción de apoyo no disponible temporalmente.)</p>
                      )}
                  </div>
                  <div className="flex-shrink-0 order-first sm:order-last">
                      <NextImage
                          src="/devmonkeys/devmonkey_coffe1_1.webp"
                          alt="Mono desarrollador disfrutando un café"
                          width={80}
                          height={80}
                          className="rounded-lg shadow-md"
                          unoptimized
                          data-ai-hint="monkey developer coffee"
                      />
                  </div>
               </div>
            </AlertDescription>
          </Alert>
      );
    } else if (currentSnapSiteData.isPremium) {
      return (
          <div className="p-4 border rounded-md bg-card mt-4">
            <h3 className="text-md font-semibold text-foreground mb-3 flex items-center">
              <Palette className="mr-2 h-5 w-5 text-primary" />
              Tema del Perfil
            </h3>
            <Controller
              name="theme"
              control={control}
              defaultValue={currentSnapSiteData?.theme || 'default'}
              render={({ field }) => (
                <RadioGroup onValueChange={field.onChange} value={field.value} className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                  {themeOptions.map((option) => (
                    <div key={option.value}>
                      <RadioGroupItem value={option.value} id={`theme-${option.value}`} className="sr-only peer" />
                      <Label htmlFor={`theme-${option.value}`} className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer transition-all">
                         <div className="flex items-center justify-center w-full h-6 mb-1.5 rounded-sm border overflow-hidden" style={{ backgroundColor: option.previewColors.card }}>
                            <div style={{ backgroundColor: option.previewColors.bg }} className="w-1/3 h-full"></div>
                            <div style={{ backgroundColor: option.previewColors.primary }} className="w-1/3 h-full"></div>
                            <div style={{ color: option.previewColors.text, backgroundColor: option.previewColors.card, display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="w-1/3 h-full text-xs">Aa</div>
                        </div>
                        <span className="text-xs text-center">{option.label}</span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}
            />
            {errors.theme && <p className="text-sm text-destructive mt-1">{errors.theme?.message}</p>}
          </div>
      );
    }
    return null; // Default case if no conditions met
  };

  return (
    <React.Fragment>
      <Card className="w-full max-w-2xl mx-auto my-8 shadow-xl">
        <CardHeader>
          <CardTitle className="text-3xl">{isDemoMode ? 'Demo SnapSite' : (isEditing ? 'Edita Tu SnapSite' : 'Crea Tu SnapSite')}</CardTitle>
           <CardDescription className="flex justify-between items-center">
            <span>
              {isDemoMode
                ? 'Prueba el editor. Los cambios se verán en la vista previa.'
                : isEditing
                  ? `Actualiza los detalles de @${usernameForDisplayAndActions}. `
                  : 'Completa los detalles para crear tu SnapSite público.'}
              {!isDemoMode && isEditing && currentSnapSiteData?.isPremium && (
                <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full dark:bg-amber-700 dark:text-amber-100">
                  🌟 Cuenta Premium
                </span>
              )}
              {!isDemoMode && isEditing && currentSnapSiteData?.status === 'published' && (
                <span className="ml-2 text-xs text-green-600 dark:text-green-400">(Publicado)</span>
              )}
              {!isDemoMode && isEditing && currentSnapSiteData?.status === 'pending_review' && (
                 <span className="ml-2 text-xs text-orange-500 dark:text-orange-400">(En revisión)</span>
              )}
            </span>
            {!isDemoMode && isEditing && currentSnapSiteData?.isPremium && paypalClientIdFromEnv && (
              <button
                type="button"
                onClick={() => setIsDonationModalOpen(true)}
                className="group p-1.5 rounded-full hover:bg-amber-100 dark:hover:bg-amber-800 transition-colors"
                aria-label="Apoyar con otro café"
              >
                <Coffee className="h-5 w-5 text-amber-600 dark:text-amber-400 group-hover:animate-wiggle" />
              </button>
            )}
          </CardDescription>
          {renderPremiumSection()}
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <Label htmlFor="username">Nombre de Usuario</Label>
                <Input 
                  id="username" 
                  {...register('username')} 
                  onChange={handleUsernameChange} 
                  placeholder="tu-nombre-unico" 
                  disabled={!usernameIsEditable && !isDemoMode} 
                  aria-describedby="username-hint"
                />
                {errors.username && <p className="text-sm text-destructive">{errors.username.message}</p>}
                {(usernameIsEditable || isDemoMode) && (
                  <div className={cn("text-xs flex items-center mt-1", usernameAvailable === true && "text-green-600", usernameAvailable === false && "text-destructive", isCheckingUsername && "text-muted-foreground")}>
                    {isCheckingUsername && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                    {usernameAvailable === true && !isCheckingUsername && <CheckCircle2 className="mr-1 h-3 w-3" />}
                    {usernameAvailable === false && !isCheckingUsername && <XCircleIconLucide className="mr-1 h-3 w-3" />}
                    {usernameFeedback}
                  </div>
                )}
                 <p id="username-hint" className="text-xs text-muted-foreground">
                    {`URL: ${watchedUsername || '[usuario]'}.${baseUserDomain}`}
                    {!usernameIsEditable && isEditing && !isDemoMode ? '. No se puede cambiar un nombre de usuario personalizado.' : ''}
                </p>
              </div>
              <div className="space-y-2"><Label htmlFor="name">Nombre Completo / Marca</Label><Input id="name" {...register('name')} placeholder="Juan Pérez" />{errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}</div>
            </div>
            <div className="space-y-2"><Label htmlFor="bio">Biografía / Descripción Corta</Label><Textarea id="bio" {...register('bio')} placeholder="Cuéntanos sobre ti..." rows={3} />{errors.bio && <p className="text-sm text-destructive">{errors.bio.message}</p>}</div>
            
            <div className="space-y-2 pt-4 border-t">
              <Label>Foto de Perfil (Máximo 10 MB, JPG/PNG/WebP)</Label>
              <div className="flex items-start gap-4">
                {previewImage ? (
                   <label htmlFor="profileImageInput" className="cursor-pointer">
                    <NextImage src={previewImage} alt="Vista previa" width={80} height={80} className="rounded-full object-cover aspect-square" unoptimized={previewImage.startsWith('blob:')} />
                  </label>
                ) : (
                   <label htmlFor="profileImageInput" className="cursor-pointer">
                    <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors">
                      <ImageIconLucide className="h-10 w-10 text-muted-foreground" />
                    </div>
                  </label>
                )}
                <div className="flex flex-col space-y-2 items-start">
                  <Controller
                    name="profileImage"
                    control={control}
                    render={({ field: { onChange, name, ref } }) => ( 
                      <label htmlFor="profileImageInput" className={cn(buttonVariants({ variant: "outline" }), "cursor-pointer inline-flex items-center")}>
                        <UploadCloud className="mr-2 h-4 w-4" /> Seleccionar Foto
                        <input
                          type="file"
                          id="profileImageInput"
                          name={name}
                          ref={imageInputRef} 
                          className="sr-only"
                          accept={ACCEPTED_IMAGE_TYPES.join(",")}
                          key={previewImage || (getValues('profileImage') ? (getValues('profileImage') as FileList)[0]?.name : 'no-file') + Date.now()}
                          onChange={(e) => {
                            const fileList = e.target.files;
                            if (!fileList || fileList.length === 0) {
                              onChange(null); 
                              setPreviewImage(initialDataProp?.profileImageUrl || null); // Reset to initial on cancel
                              setValue('removeProfileImage', false); 
                              if (imageInputRef.current) imageInputRef.current.value = '';
                              return;
                            }
                            const file = fileList[0];
                            if (file.size > MAX_FILE_SIZE) {
                              toast({ variant: "destructive", title: "Archivo Demasiado Grande", description: `La imagen no debe exceder los ${MAX_FILE_SIZE / (1024 * 1024)} MB.` });
                              if (imageInputRef.current) imageInputRef.current.value = '';
                              onChange(null); 
                              return;
                            }
                            if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
                              toast({ variant: "destructive", title: "Tipo de Archivo Inválido", description: "Solo se aceptan imágenes JPEG, PNG, o WebP." });
                              if (imageInputRef.current) imageInputRef.current.value = '';
                              onChange(null); 
                              return;
                            }
                            
                            const oldPreview = previewImage;
                            const newObjectURL = URL.createObjectURL(file);
                            setPreviewImage(newObjectURL); // This sets the state for preview
                            if (oldPreview && oldPreview.startsWith('blob:') && newObjectURL !== oldPreview) {
                                URL.revokeObjectURL(oldPreview);
                            }
                            
                            setValue('removeProfileImage', false, { shouldDirty: true });
                            onChange(fileList); // This sets the RHF value with the FileList
                          }}
                        />
                      </label>
                    )}
                  />
                  {profileImageFileWatch && profileImageFileWatch.length > 0 && profileImageFileWatch[0] instanceof File && (
                    <p className="text-xs text-muted-foreground">Archivo seleccionado: {profileImageFileWatch[0].name}</p>
                  )}
                  {previewImage && !watch('removeProfileImage') && (
                    <Button type="button" variant="ghost" size="sm" className="text-xs text-destructive hover:text-destructive/80 hover:bg-destructive/10 h-auto p-1"
                      onClick={() => {
                        setValue('removeProfileImage', true, { shouldDirty: true });
                        setValue('profileImage', undefined, { shouldDirty: true }); 
                        if (imageInputRef.current) imageInputRef.current.value = ''; 
                        setPreviewImage(null);
                      }}>
                      <XCircleIconLucide className="mr-1 h-3 w-3" /> Eliminar foto actual
                    </Button>
                  )}
                </div>
              </div>
              {errors.profileImage && <p className="text-sm text-destructive mt-1">{(errors.profileImage as any)?.message || 'Archivo inválido'}</p>}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono (Opcional)</Label>
                <div className="flex items-center gap-2">
                  <PhoneIcon className="h-5 w-5 text-muted-foreground" />
                  <Input id="phone" {...register('phone')} type="tel" placeholder="+52 55 1234 5678" />
                </div>
                {errors.phone && <p className="text-sm text-destructive">{errors.phone.message}</p>}
                 <Controller
                    name="usePhoneForWhatsapp"
                    control={control}
                    render={({ field }) => (
                        <div className="flex items-center space-x-2 mt-1">
                        <Checkbox 
                            id="usePhoneForWhatsapp"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            aria-label="Usar mi número de teléfono para WhatsApp"
                        />
                        <Label htmlFor="usePhoneForWhatsapp" className="text-xs font-normal text-muted-foreground">Usar mi número de teléfono para WhatsApp</Label>
                        </div>
                    )}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Ubicación (Ciudad, País - Opcional)</Label>
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                  <Input id="location" {...register('location')} placeholder="Ciudad de México, México" />
                </div>
                {errors.location && <p className="text-sm text-destructive">{errors.location.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="googleMapsLink">Enlace a Google Maps (Opcional)</Label>
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                  <Input id="googleMapsLink" {...register('googleMapsLink')} type="url" placeholder="https://www.google.com/maps/..." />
                </div>
                {errors.googleMapsLink && <p className="text-sm text-destructive">{errors.googleMapsLink.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Empresa (Opcional)</Label>
                <div className="flex items-center gap-2">
                  <BuildingIcon className="h-5 w-5 text-muted-foreground" />
                  <Input id="company" {...register('company')} placeholder="Nombre de tu Empresa" />
                </div>
                {errors.company && <p className="text-sm text-destructive">{errors.company.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="position">Cargo / Puesto (Opcional)</Label>
                <div className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-muted-foreground" />
                  <Input id="position" {...register('position')} placeholder="Desarrollador Web" />
                </div>
                {errors.position && <p className="text-sm text-destructive">{errors.position.message}</p>}
              </div>
            </div>
            
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-medium">Redes Sociales (Opcional)</h3>
              {socialLinkFieldsInfo.map(fieldInfo => (
                <div key={fieldInfo.name} className="space-y-1">
                  <Label htmlFor={`socialLinks.${fieldInfo.name}`}>{fieldInfo.label}</Label>
                  <div className="flex items-center gap-2">
                    <fieldInfo.icon className="h-5 w-5 text-muted-foreground" />
                    <Input 
                      id={`socialLinks.${fieldInfo.name}`} 
                      type="url" 
                      {...register(`socialLinks.${fieldInfo.name}` as const)} 
                      placeholder={fieldInfo.placeholder} 
                      onBlur={(e) => fieldInfo.name === 'whatsapp' ? handleWhatsappBlur(e) : handleUrlBlur(e, `socialLinks.${fieldInfo.name}` as const)}
                      disabled={fieldInfo.name === 'whatsapp' && usePhoneForWhatsappWatch && !!phoneWatch}
                    />
                  </div>
                  {errors.socialLinks?.[fieldInfo.name] && <p className="text-sm text-destructive">{errors.socialLinks[fieldInfo.name]?.message}</p>}
                </div>
              ))}
            </div>

            <div className="space-y-4 pt-4 border-t">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Enlaces Personales (Máximo 5, Opcional)</h3>
                {personalLinkFields.length < 5 && (
                  <Button type="button" variant="outline" size="sm" onClick={() => appendPersonalLink({ name: '', url: '' })}>
                    <PlusCircle className="mr-2 h-4 w-4" />Añadir Enlace Personal
                  </Button>
                )}
              </div>
              {personalLinkFields.map((field, index) => (
                <div key={field.id} className="flex items-end gap-2 p-3 border rounded-md bg-muted/20 dark:bg-muted/10">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor={`personalLinks[${index}].name`}>Nombre del Enlace</Label>
                    <Input {...register(`personalLinks.${index}.name`)} placeholder="Ej: Mi Portafolio"/>
                    {errors.personalLinks?.[index]?.name && (<p className="text-sm text-destructive">{errors.personalLinks[index]?.name?.message}</p>)}
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label htmlFor={`personalLinks[${index}].url`}>URL</Label>
                    <Input {...register(`personalLinks.${index}.url`)} type="url" placeholder="https://ejemplo.com/portafolio" onBlur={(e) => handleUrlBlur(e, `personalLinks.${index}.url` as const)} />
                    {errors.personalLinks?.[index]?.url && (<p className="text-sm text-destructive">{errors.personalLinks[index]?.url?.message}</p>)}
                  </div>
                  <Button type="button" variant="destructive" size="icon" onClick={() => removePersonalLink(index)} aria-label="Eliminar enlace"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              {errors.personalLinks && typeof errors.personalLinks.message === 'string' && (<p className="text-sm text-destructive">{errors.personalLinks.message}</p>)}
            </div>

            {!isEditing && !isDemoMode && (
              <div className="items-top flex space-x-2 pt-4 border-t">
                <Controller 
                  name="acceptTerms" 
                  control={control} 
                  render={({ field }) => (
                    <Checkbox 
                      id="acceptTerms" 
                      checked={field.value} 
                      onCheckedChange={field.onChange} 
                      aria-describedby="acceptTerms-message"
                    />
                  )}
                />
                <div className="grid gap-1.5 leading-none">
                  <Label htmlFor="acceptTerms" className="text-sm font-medium">
                    Acepto los{' '}
                    <NextLink href="/terminos" className="font-medium text-primary hover:underline" target="_blank">
                      Términos y Condiciones
                    </NextLink>.
                  </Label>
                  {errors.acceptTerms && (<p id="acceptTerms-message" className="text-sm text-destructive">Debes aceptar los términos.</p>)}
                </div>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full" disabled={isSubmitDisabled}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {!loading && (isDemoMode ? <RefreshCw className="mr-2 h-4 w-4" /> : (isEditing ? <Save className="mr-2 h-4 w-4" /> : <UserPlus className="mr-2 h-4 w-4" />))}
              {loading ? (isDemoMode ? 'Actualizando Demo...' : 'Guardando...') : (isDemoMode ? 'Actualizar Vista Previa Demo' : (isEditing ? 'Actualizar SnapSite' : 'Crear SnapSite'))}
            </Button>
          </CardFooter>
        </form>
      </Card>
      
       {!isDemoMode && isEditing && usernameForDisplayAndActions && (
         <div className="w-full max-w-2xl mx-auto mt-6 text-left"> 
          <AlertDialog open={isDeleteDialogOpen} onOpenChange={(open) => {
              setIsDeleteDialogOpen(open);
              if (!open) setDeleteConfirmationText(''); 
          }}>
            <AlertDialogTrigger asChild>
              <button 
                 className="inline-flex items-center text-sm font-medium text-red-700 hover:text-red-900 dark:text-red-500 dark:hover:text-red-400 cursor-pointer p-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Eliminar mi SnapSite
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center"><AlertTriangle className="mr-2 h-6 w-6 text-destructive"/>Confirmar Eliminación</AlertDialogTitle>
                <AlertDialogDescription>Esta acción es irreversible y eliminará permanentemente todos los datos de tu SnapSite, incluyendo tu perfil y foto. Escribe "<b>Quiero Eliminar mis datos</b>" para confirmar.</AlertDialogDescription>
              </AlertDialogHeader>
              <Input type="text" value={deleteConfirmationText} onChange={(e) => setDeleteConfirmationText(e.target.value)} placeholder='Escribe la frase aquí' className="my-4"/>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => { setIsDeleteDialogOpen(false); setDeleteConfirmationText(''); }}>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteSnapSite} disabled={deleteConfirmationText !== "Quiero Eliminar mis datos" || isDeleting} className={cn(buttonVariants({variant: "destructive"}))}>
                  {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}{isDeleting ? 'Eliminando...' : 'Eliminar Definitivamente'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {paypalClientIdFromEnv && user && !isDemoMode && // Donación solo en modo real por ahora
        <DonationModal
            isOpen={isDonationModalOpen}
            onClose={() => setIsDonationModalOpen(false)}
            paypalClientId={paypalClientIdFromEnv}
            onPaymentSuccess={handlePaymentSuccess}
        />
      }
    </React.Fragment>
  );
}
